import React, { Component } from 'react'
import PropTypes from 'prop-types'
import cx from 'classnames'
import _ from 'lodash'
import { injectIntl, FormattedMessage } from 'react-intl'
import { ColumnColorChart } from 'gnos-charts'
import { DataTable, DATA_TABLE_CONSTANTS as CONSTANTS } from 'gnos-tables'

import config from '../../../../../../../../../config'
import { createDataTableId } from '../../../../../../helpers'

import { LOCALE_PREFIX } from './constants'
import { ENTITY_CONTEXT_MENU_ITEM } from '../../../../../../../components/Workspace/components/EntityContextMenu/constants'

import NoData from '../../../../../../../components/Workspace/components/NoData'

import EntityContextMenu from '../../../../../../../components/Workspace/components/EntityContextMenu'
import Flag from '../../../../../../../../../components/TableCell/components/Flag'
import Money from '../../../../../../../../../components/TableCell/components/Money'

import hocBlock from '../../../../../../../components/Workspace/components/HocBlock'

import './style.scss'

@hocBlock(config.modules.portfolio.key, { levelReload: true })
@injectIntl
export default class BisnodeScoring extends Component {
    static propTypes = {
        block: PropTypes.string.isRequired,
        data: PropTypes.object,
        id: PropTypes.string.isRequired,
        intl: PropTypes.object.isRequired,
        level: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        locale: PropTypes.string.isRequired,
        token: PropTypes.string.isRequired,
    }

    shouldComponentUpdate(nextProps) {
        if (this.props.block !== nextProps.block) return true
        if (!_.isEqual(this.props.data !== nextProps.data)) return true
        if (this.props.id !== nextProps.id) return true
        if (this.props.level !== nextProps.level) return true
        if (this.props.locale !== nextProps.locale) return true
        if (this.props.token !== nextProps.token) return true

        return false
    }

    getColumnDefinitions = () => {
        const { level } = this.props

        const commonColumns = [
            {
                Header: '',
                accessor: 'country',
                Cell: ({ value }) => <Flag country={value} />,
                width: 32,
                resizable: false,
            },
            {
                Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.name`} />,
                accessor: 'name',
                width: 300,
            },
        ]

        let levelColumns = []
        switch (level) {
            case 30: {
                levelColumns = [
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.interval`} />,
                        accessor: 'longTxt',
                        showArrow: {
                            direction: CONSTANTS.DATA_TABLE_ARROW.DOWN,
                            text: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.interval.sort`} />,
                        },
                    },
                ]
                break
            }
            case 50: {
                levelColumns = [
                    {
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.scoring`} />,
                        accessor: 'longTxt',
                        showArrow: {
                            direction: CONSTANTS.DATA_TABLE_ARROW.DOWN,
                            text: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.scoring.sort`} />,
                        },
                    },
                    {
                        id: 'creditLimit',
                        Header: <FormattedMessage id={`${LOCALE_PREFIX}.table.header.creditLimit`} />,
                        accessor: ({ creditLimit, currency }) => ({ creditLimit, currency }),
                        Cell: ({ value: { creditLimit, currency } }) => {
                            return (
                                <Money decimalPlaces={2} value={{ amount: creditLimit, currency }} emptyValue={'-'} />
                            )
                        },
                    },
                ]
                break
            }
            default:
                levelColumns = []
        }

        return [...commonColumns, ...levelColumns]
    }

    renderSummary = (dataProvider, blockData) => {
        const textIdPrefix = 'scenes.Gnosus.scenes.Portfolio.components.Workspace.components.Blocks.BisnodeScoring'
        const ratingColor = _.get(
            _.find(dataProvider, (d) => d.category === blockData.ratingAvg),
            'color',
            '#000000'
        )

        const average = blockData.ratingAvg ? (
            <div className={'average'}>
                <div className={'rating'} style={{ color: ratingColor }}>
                    {blockData.ratingAvg}
                </div>
                <div className={'texts'}>
                    <div>
                        <FormattedMessage id={`${textIdPrefix}.average.line1`} />
                    </div>
                    <div>
                        <FormattedMessage id={`${textIdPrefix}.average.line2`} />
                    </div>
                </div>
            </div>
        ) : null

        return (
            <div className={'summary'}>
                {average}
                <div className={'dataTable'}>
                    <div className={'titles'}>
                        <div>
                            <FormattedMessage id={`${textIdPrefix}.wholeCount`} />
                        </div>
                        <div>{blockData.ratingMedian ? <FormattedMessage id={`${textIdPrefix}.median`} /> : null}</div>
                    </div>
                    <div className={'values'}>
                        <div>{blockData.cnt}</div>
                        <div>{blockData.ratingMedian}</div>
                    </div>
                </div>
            </div>
        )
    }

    render() {
        const { data, block, id, level, locale, token, intl } = this.props

        const blockData = _.get(data, 'data', null)
        const dataProvider = _.get(blockData, 'chartData', null)

        if (!blockData) return null

        if (!dataProvider) {
            return <NoData text={<FormattedMessage id={`${LOCALE_PREFIX}.noData`} />} />
        }

        return (
            <div className={cx('block-bisnode-scoring')}>
                {this.renderSummary(dataProvider, blockData)}
                <ColumnColorChart
                    style={{
                        width: '100%',
                        height: '350px',
                    }}
                    dataProvider={dataProvider}
                    showAxisY={false}
                    greyColumnLabels={['NA', 'N/A', '-']}
                />
                <DataTable
                    id={createDataTableId(config.modules.portfolio.key, id, block)}
                    uriSettings={_.merge({}, config.api.portfolio[block].table, {
                        qs: { groupId: id, level, token, lang: config.languages[locale].requestKey },
                    })}
                    urlSettingsReloadPaths={[{ path: 'qs.level', reset: true }, { path: 'qs.lang' }]}
                    columnsDefinition={this.getColumnDefinitions()}
                    hide={level < 30}
                    contextMenu={
                        <EntityContextMenu
                            id={id}
                            items={[
                                ENTITY_CONTEXT_MENU_ITEM.ADD_FAVOURITE,
                                ENTITY_CONTEXT_MENU_ITEM.REMOVE_FAVOURITE,
                                ENTITY_CONTEXT_MENU_ITEM.REPORT,
                                ENTITY_CONTEXT_MENU_ITEM.LINKAGES,
                                ENTITY_CONTEXT_MENU_ITEM.DISTRAINTS,
                            ]}
                        />
                    }
                    texts={{
                        previousText: intl.formatMessage({ id: 'global.previous' }),
                        nextText: intl.formatMessage({ id: 'global.next' }),
                        loadingText: intl.formatMessage({ id: 'global.loading' }),
                        noDataText: intl.formatMessage({ id: 'global.noData' }),
                        pageText: intl.formatMessage({ id: 'global.page' }),
                        ofText: intl.formatMessage({ id: 'global.of' }),
                        rowsText: intl.formatMessage({ id: 'global.rows' }),
                    }}
                    itemIdentifier={'entId'}
                    showPageSizeOptions
                />
            </div>
        )
    }
}
